import { Accordion, Button, Checkbox, Form, Header, Icon, Input, Segment } from 'semantic-ui-react'
import { UltyDropdown } from '@ulty/ulty-ui'
import { useTranslation } from 'react-i18next'
import { usePricerOptions } from '../../services/usePricerOptions'
import { usePricerNetworkDiscovery } from '../../services/usePricerNetworkDiscovery'
import { flashItems } from '../../services/pricer.service'
import useFeature from '../../../components/Shared/FeatureProvider/useFeature'
import { useEffect, useRef, useState } from 'react'

const AdminPricerOptions = () => {
  const [t] = useTranslation()
  const [options, setOptions] = usePricerOptions()
  const { isSearching, discover } = usePricerNetworkDiscovery()
  const { isEnabled } = useFeature()
  const [isCheckingConnectivity, setIsCheckingConnectivity] = useState(false)
  const [isConnectivityOk, setIsConnectivityOk] = useState(undefined)
  const connectivityDebounceTimeoutRef = useRef(null)
  const [portInputValue, setPortInputValue] = useState(String(options.port ?? ''))

  const isEnabledFeature = isEnabled('integration.pricer')

  useEffect(() => {
    setPortInputValue(String(options.port ?? ''))
  }, [options.port])

  useEffect(() => () => {
    if (connectivityDebounceTimeoutRef.current) {
      clearTimeout(connectivityDebounceTimeoutRef.current)
    }
  }, [])

  const handlePortChange = ({ target }) => {
    setPortInputValue(target.value)
  }

  const commitPortValue = () => {
    if (portInputValue === '') {
      setPortInputValue(String(options.port ?? ''))
      return
    }

    const port = parseInt(portInputValue, 10)

    if (!isNaN(port) && (port === 80 || (port >= 1000 && port <= 65535))) {
      if (port !== options.port) {
        update({ port })
      }
      setPortInputValue(String(port))
      return
    }

    setPortInputValue(String(options.port ?? ''))
  }

  if (!window.Android || !isEnabledFeature) return null

  const handleDiscover = () => {
    discover(options.port, (address) => {
      if (address) update({ serverAddress: address })
    })
  }

  const handleCheckConnectivity = async (connectivityOptions = options) => {
    if (connectivityDebounceTimeoutRef.current) {
      clearTimeout(connectivityDebounceTimeoutRef.current)
    }

    if (!connectivityOptions.serverAddress) {
      setIsConnectivityOk(false)
      setIsCheckingConnectivity(false)
      return
    }

    setIsCheckingConnectivity(true)
    setIsConnectivityOk(undefined)
    connectivityDebounceTimeoutRef.current = setTimeout(async () => {
      const success = await flashItems(connectivityOptions.serverAddress, connectivityOptions.port, connectivityOptions.auth_key, ["0000000000000"])
      setIsConnectivityOk(success)
      setIsCheckingConnectivity(false)
    }, 200)
  }

  const update = (patch) => {
    setOptions((prev) => {
      const next = { ...prev, ...patch }

      if (!next.enabled || !next.serverAddress) {
        if (connectivityDebounceTimeoutRef.current) {
          clearTimeout(connectivityDebounceTimeoutRef.current)
        }
        setIsCheckingConnectivity(false)
        setIsConnectivityOk(undefined)
      } else {
        handleCheckConnectivity(next)
      }

      return next
    })
  }

  const connectivityLabel =
    isConnectivityOk === true
      ? t('pricer.admin.check_connectivity_success')
      : isConnectivityOk === false
        ? t('pricer.admin.check_connectivity_error')
        : t('pricer.admin.check_connectivity_button')

  return (
    <Segment>
      <Header>{t('pricer.admin.header')}</Header>
      <Form>
        <Form.Field>
          <Checkbox
            toggle
            label={t('pricer.admin.enabled_label')}
            checked={options.enabled}
            onChange={(_, { checked }) => update({ enabled: checked })}
          />
        </Form.Field>

        {options.enabled && (
          <>
            <UltyDropdown
              label={t('pricer.admin.trigger_mode_label')}
              options={[
                { key: 'MANUAL', value: 0, text: t('pricer.admin.trigger_mode.MANUAL') },
                { key: 'NEXT_1', value: 1, text: t('pricer.admin.trigger_mode.NEXT_1') },
                { key: 'NEXT_2', value: 2, text: t('pricer.admin.trigger_mode.NEXT_2') },
              ]}
              multiple={false}
              value={options.triggerMode}
              onChange={(_, { value }) => update({ triggerMode: value })}
            />

            <Accordion>
              <Accordion.Title
                active={options.advancedEnabled}
                onClick={() => update({ advancedEnabled: !options.advancedEnabled })}
                icon={`caret ${options.advancedEnabled ? 'down' : 'right'}`}
                content={t('pricer.admin.advanced_configuration')}
              />
              <Accordion.Content active={options.advancedEnabled}>
                <Form.Field>
                  <label>{t('pricer.admin.server_address_label')}</label>
                  <Input
                    value={options.serverAddress}
                    onChange={({ target }) => update({ serverAddress: target.value })}
                  />
                </Form.Field>
                <Form.Field>
                  <label>{t('pricer.admin.port_label')}</label>
                  <Input
                    type="number"
                    min={80}
                    max={65535}
                    value={portInputValue}
                    onChange={handlePortChange}
                    onBlur={commitPortValue}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        commitPortValue()
                      }
                    }}
                  />
                </Form.Field>
                {/* <Button
                  loading={isSearching}
                  disabled={true ||isSearching}
                  onClick={handleDiscover}
                  content={t('pricer.admin.discover_button')}
                /> */}
                <Button
                  loading={isCheckingConnectivity}
                  disabled={isCheckingConnectivity}
                  onClick={handleCheckConnectivity}
                  icon={isConnectivityOk === true ? 'check circle' : isConnectivityOk === false ? 'times circle' : undefined}
                  color={isConnectivityOk === true ? 'green' : isConnectivityOk === false ? 'red' : undefined}
                  basic={isConnectivityOk !== undefined}
                >
                  {connectivityLabel}
                </Button>
              </Accordion.Content>
            </Accordion>
          </>
        )}
      </Form>
    </Segment>
  )
}

export { AdminPricerOptions }
